import { useMemo, useState } from "react";
import { classNames } from "primereact/utils";
import {
  IInventoryCategoryModel,
  IInventoryItemModel,
} from "../hooks/useCashdeskPage";
import { IInventoryProductSearchResult } from "../models/cashdeskModels";
import { IGiftVoucherModel } from "../../administration/giftVouchers/models/giftVoucherModels";
import { BarcodeScannerModal } from "../../../common/components/forms/inputs/BarcodeField/BarcodeScannerModal";
import { Icon, IconType } from "../../../common/icons/Icon";
import styles from "./CashdeskProductGrid.module.scss";

interface ICashdeskProductGridProps {
  categories: IInventoryCategoryModel[];
  items: IInventoryItemModel[];
  isLoading: boolean;
  activeCategoryId: number | null;
  setActiveCategoryId: (id: number | null) => void;
  productSearch: {
    searchQuery: string;
    setSearchQuery: (q: string) => void;
    searchResults: IInventoryProductSearchResult[];
    handleBarcodeScanned: (value: string) => void;
  };
  onProductClick: (product: IInventoryProductSearchResult) => void;
  /** This studio's unsold, unexpired vouchers. Never carries their codes. */
  sellableVouchers: IGiftVoucherModel[];
  onVoucherClick: (voucher: IGiftVoucherModel) => void;
  /** Voucher ids already in the cart — a voucher is a unique item, sold exactly once. */
  cartVoucherIds: number[];
}

/**
 * Sentinel id for the virtual "Ваучери" chip. Vouchers are not inventory, so they have
 * no InventoryCategory to hang off; a negative id can never collide with a real one.
 */
const VOUCHERS_CATEGORY_ID = -1;

export const CashdeskProductGrid = ({
  categories,
  items,
  isLoading,
  activeCategoryId,
  setActiveCategoryId,
  productSearch,
  onProductClick,
  sellableVouchers,
  onVoucherClick,
  cartVoucherIds,
}: ICashdeskProductGridProps) => {
  const [clickedProductId, setClickedProductId] = useState<number | null>(null);
  const [isScannerOpen, setIsScannerOpen] = useState(false);

  const sortedCategories = useMemo(
    () =>
      [...categories]
        .filter((c) => !c.isDeleted)
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [categories],
  );

  const filteredItems = useMemo(() => {
    let filtered = items.filter((i) => !i.isDeleted);

    if (activeCategoryId !== null) {
      filtered = filtered.filter((i) => i.categoryId === activeCategoryId);
    }

    if (productSearch.searchQuery.trim()) {
      const q = productSearch.searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (i) =>
          i.name.toLowerCase().includes(q) ||
          (i.brand && i.brand.toLowerCase().includes(q)) ||
          (i.barcode && i.barcode.toLowerCase().includes(q)) ||
          (i.sku && i.sku.toLowerCase().includes(q)),
      );
    }

    return filtered.sort((a, b) => a.sortOrder - b.sortOrder);
  }, [items, activeCategoryId, productSearch.searchQuery]);

  const isVouchersTab = activeCategoryId === VOUCHERS_CATEGORY_ID;

  const filteredVouchers = useMemo(() => {
    const query = productSearch.searchQuery.toLowerCase().trim();

    return query
      ? sellableVouchers.filter((voucher) =>
          voucher.name.toLowerCase().includes(query),
        )
      : sellableVouchers;
  }, [sellableVouchers, productSearch.searchQuery]);

  const handleProductClick = (item: IInventoryItemModel) => {
    const category = categories.find((c) => c.id === item.categoryId);
    const product: IInventoryProductSearchResult = {
      id: item.id,
      name: item.name,
      brand: item.brand,
      emoji: category?.emoji ?? "📦",
      sku: item.sku,
      barcode: item.barcode,
      currentQuantity: item.currentQuantity,
      unitOfMeasure: item.unitOfMeasure,
      lowStockThreshold: item.lowStockThreshold,
      price: item.price,
    };

    setClickedProductId(item.id);
    setTimeout(() => setClickedProductId(null), 300);
    onProductClick(product);
  };

  return (
    <>
      <div className={styles.search_row}>
        <div className={styles.search_wrap} data-onboarding="cashdesk-search">
          <input
            className={styles.search_input}
            type="text"
            placeholder="Търси продукт или баркод..."
            value={productSearch.searchQuery}
            onChange={(e) => productSearch.setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                productSearch.handleBarcodeScanned(productSearch.searchQuery);
              }
            }}
          />
        </div>
        <button
          className={styles.barcode_btn}
          onClick={() => setIsScannerOpen(true)}
          title="Баркод скенер"
          data-onboarding="cashdesk-barcode-btn"
        >
          <Icon type={IconType.Camera} width="18px" height="18px" />
        </button>
        <BarcodeScannerModal
          visible={isScannerOpen}
          onHide={() => setIsScannerOpen(false)}
          onScanned={(value) => {
            setIsScannerOpen(false);
            productSearch.handleBarcodeScanned(value);
          }}
        />
      </div>

      <div className={styles.categories}>
        {isLoading ? (
          <>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={styles.skeleton_chip} />
            ))}
          </>
        ) : (
          <>
            <button
              className={classNames(styles.category_chip, { [styles.category_chip_active]: activeCategoryId === null })}
              onClick={() => setActiveCategoryId(null)}
            >
              Всички
            </button>
            {/* Right after "Всички", never at the end: the chip row scrolls sideways and a
                studio with a few categories would otherwise push vouchers off screen. It is
                only offered when there is something to sell. */}
            {sellableVouchers.length > 0 && (
              <button
                className={classNames(styles.category_chip, { [styles.category_chip_active]: isVouchersTab })}
                onClick={() => setActiveCategoryId(VOUCHERS_CATEGORY_ID)}
              >
                🎁 Ваучери ({sellableVouchers.length})
              </button>
            )}
            {sortedCategories.map((cat) => (
              <button
                key={cat.id}
                className={classNames(styles.category_chip, { [styles.category_chip_active]: activeCategoryId === cat.id })}
                onClick={() => setActiveCategoryId(cat.id)}
              >
                {cat.emoji} {cat.name}
              </button>
            ))}
          </>
        )}
      </div>

      <div className={styles.grid}>
        {isLoading && (
          <>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className={styles.skeleton_card}>
                <div className={styles.skeleton_card_emoji} />
                <div className={styles.skeleton_card_name} />
                <div className={styles.skeleton_card_price} />
                <div className={styles.skeleton_card_stock} />
              </div>
            ))}
          </>
        )}
        {!isLoading && !isVouchersTab && filteredItems.length === 0 && (
          <div className={styles.empty}>Няма намерени продукти</div>
        )}

        {isVouchersTab && filteredVouchers.length === 0 && (
          <div className={styles.empty}>Няма ваучери за продажба</div>
        )}

        {/*
          Voucher cards. The code is deliberately NOT shown: this grid is visible to
          instructors and the code is what lets anyone spend the balance. A voucher
          already in the cart is greyed out — it is a unique instrument, sold once.
        */}
        {isVouchersTab &&
          filteredVouchers.map((voucher) => {
            const isInCart = cartVoucherIds.includes(voucher.id);

            return (
              <div
                key={voucher.id}
                className={classNames(styles.product, {
                  [styles.product_adding]: isInCart,
                })}
                onClick={() => !isInCart && onVoucherClick(voucher)}
              >
                <div className={styles.product_emoji}>🎁</div>
                <div className={styles.product_name}>{voucher.name}</div>
                <div className={styles.product_price}>
                  {voucher.initialAmount.toFixed(2)} €
                </div>
                <div className={styles.product_stock}>
                  {isInCart
                    ? "В количката"
                    : voucher.validUntil
                      ? `До ${new Date(voucher.validUntil).toLocaleDateString("bg-BG")}`
                      : "Безсрочен"}
                </div>
              </div>
            );
          })}

        {!isVouchersTab && filteredItems.map((item, index) => {
          const category = categories.find((c) => c.id === item.categoryId);
          const isLowStock =
            item.currentQuantity <= item.lowStockThreshold;
          const displayName = item.brand
            ? `${item.name} - ${item.brand}`
            : item.name;

          return (
            <div
              key={item.id}
              className={classNames(styles.product, { [styles.product_adding]: clickedProductId === item.id })}
              onClick={() => handleProductClick(item)}
              {...(index === 0 ? { "data-onboarding": "cashdesk-first-product" } : {})}
            >
              <div className={styles.product_emoji}>
                {category?.emoji ?? "📦"}
              </div>
              <div className={styles.product_name}>{displayName}</div>
              <div className={styles.product_price}>
                {item.price.toFixed(2)} €
              </div>
              <div
                className={classNames(styles.product_stock, { [styles.product_stock_low]: isLowStock })}
              >
                Наличност: {item.currentQuantity}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};
